<template>
  <dx-modal :value="show"
            title="Fechar caixa"
            size="lg"
            :closable="!saving"
            @input="close()"
            id="modal-cash-register-close">
    <template v-if="closed">
      <div class="cr-box done">
        <div class="empty">
          <div class="empty-icon"><fa-icon :icon="['fal', 'check-circle']" size="2x"/></div>
          <p class="empty-title h5">Caixa fechado às {{ closed.closing.date | date('time') }}</p>
          <p class="empty-subtitle">
            Dinheiro contado {{ closed.closing.counted[0].value | currency }} ·
            esperado {{ closed.closing.expected[0].value | currency }} ·
            diferença <strong>{{ closed.closing.cashDifference | currency }}</strong>
            <span v-if="closed.review.status === 'flagged'">
              · sinalizado para a tesouraria
            </span>
          </p>
        </div>
      </div>
      <p class="text-gray mb-0">
        Imprima o fechamento, confira com o gerente e leve o dinheiro da sangria à tesouraria.
      </p>
    </template>

    <template v-else>
      <div class="cr-summary">
        <div class="cr-summary-item text-left">
          <small>Aberto às</small>
          <div class="cr-summary-value">{{ session.opening.date | date('time') }}</div>
        </div>
        <div class="cr-summary-item">
          <small>Fundo de troco</small>
          <div class="cr-summary-value">{{ session.opening.fund | currency }}</div>
        </div>
        <div class="cr-summary-item">
          <small>Movimentos</small>
          <div class="cr-summary-value">{{ session.totals.entries }}</div>
        </div>
        <div class="cr-summary-item">
          <small>Recebido</small>
          <div class="cr-summary-value">{{ netReceipts | currency }}</div>
        </div>
      </div>

      <div class="cr-box cr-count" :class="countStateClass">
        <div class="cr-count-text">
          <div class="cr-box-title">1. Conte o dinheiro da gaveta</div>
          <p class="cr-count-hint" v-if="!countState">
            Some notas e moedas, inclusive o fundo de troco.
            <template v-if="blind">O valor esperado só aparece depois da contagem.</template>
            <template v-else>
              Esperado na gaveta: <strong>{{ expectedCash | currency }}</strong>.
            </template>
          </p>
          <p class="cr-count-hint" v-else-if="countState === 'ok'">
            <fa-icon :icon="['fal', 'check-circle']" class="mr-1"/>
            Bateu com o esperado. Se errou, <a href="#" @click.prevent="recount">conte de novo</a>.
          </p>
          <p class="cr-count-hint" v-else-if="countState === 'warn'">
            <fa-icon :icon="['fal', 'exclamation-triangle']" class="mr-1"/>
            Diferença de <strong>{{ cashDifference | currency }}</strong>, dentro da tolerância
            de {{ tolerance | currency }}. Se errou, <a href="#" @click.prevent="recount">conte de
            novo</a>.
          </p>
          <p class="cr-count-hint" v-else>
            <fa-icon :icon="['fal', 'exclamation-triangle']" class="mr-1"/>
            <strong>Fora da tolerância.</strong> Diferença de
            <strong>{{ cashDifference | currency }}</strong> para o esperado de
            {{ expectedCash | currency }}<template v-if="tolerance > 0">
              (tolerância de {{ tolerance | currency }})</template>.
            Confira a gaveta e, se errou, <a href="#" @click.prevent="recount">conte de novo</a>.
          </p>
        </div>
        <div class="cr-count-field form-group" :class="{'has-error': $v.form.countedCash.$error}">
          <label class="form-label" for="counted-cash">Dinheiro contado</label>
          <div class="input-group">
            <span class="input-group-addon addon-lg">R$</span>
            <dx-input-number id="counted-cash"
                             v-model="form.countedCash"
                             class="form-input input-lg text-right text-bold"
                             :precision="2"
                             maxlength="10"
                             :disabled="blind && step === 2"
                             @keypress.enter="reveal"
                             @blur="$v.form.countedCash.$touch()"/>
          </div>
          <div class="form-input-hint" v-if="$v.form.countedCash.$error">
            Informe o valor contado
          </div>
        </div>
      </div>

      <template v-if="step === 2">
        <div class="cr-box">
          <div class="cr-box-title">2. Conferência por forma de pagamento</div>
          <table class="table">
            <thead>
            <tr>
              <th>Forma</th>
              <th class="text-right">Esperado</th>
              <th class="text-right">Informado</th>
              <th class="text-right">Diferença</th>
            </tr>
            </thead>
            <tbody>
            <tr>
              <td><strong>Dinheiro em caixa</strong></td>
              <td class="text-right">{{ expectedCash | currency }}</td>
              <td class="text-right">{{ form.countedCash | currency }}</td>
              <td class="text-right text-bold" :class="differenceClass(cashDifference)">
                {{ cashDifference | currency }}
              </td>
            </tr>
            <tr v-for="line in otherMethods" :key="line.method.id">
              <td>{{ line.method.name }}</td>
              <td class="text-right">{{ line.value | currency }}</td>
              <td class="text-right" style="width: 160px">
                <dx-input-number v-model="counted[line.method.id]"
                                 class="form-input input-sm text-right"
                                 :precision="2"
                                 maxlength="10"/>
              </td>
              <td class="text-right text-bold"
                  :class="differenceClass(counted[line.method.id] - line.value)">
                {{ (counted[line.method.id] - line.value) | currency }}
              </td>
            </tr>
            </tbody>
          </table>
          <small class="text-gray">
            A diferença fica registrada no caixa. O gestor decide o que fazer na tesouraria.
          </small>
        </div>

        <div class="cr-box">
          <div class="cr-box-title">
            3. Destino do dinheiro contado ({{ form.countedCash | currency }})
          </div>
          <div class="columns">
            <div class="column col-4 col-sm-12 form-group"
                 :class="{'has-error': $v.form.keptAsFund.$error}">
              <label class="form-label">Fica na gaveta como fundo</label>
              <dx-input-number id="kept-as-fund"
                               v-model="form.keptAsFund"
                               class="form-input text-right"
                               :precision="2"
                               maxlength="10"
                               @change="syncWithdrawal"
                               @blur="$v.form.keptAsFund.$touch()"/>
              <template v-if="$v.form.keptAsFund.$error">
                <div class="form-input-hint">Entre zero e o valor contado</div>
              </template>
            </div>
            <div class="column col-3 col-sm-12 form-group">
              <label class="form-label">Sangria de fechamento</label>
              <input type="text"
                     class="form-input text-right"
                     readonly
                     :value="withdrawalValue | currency">
            </div>
            <div class="column col-5 col-sm-12 form-group" v-if="withdrawalValue > 0"
                 :class="{'has-error': $v.form.withdrawalBankAccountId.$error}">
              <label class="form-label">Conta de destino da sangria</label>
              <select class="form-select" v-model="form.withdrawalBankAccountId"
                      @blur="$v.form.withdrawalBankAccountId.$touch()">
                <option value="">[Selecione]</option>
                <option v-for="item in bankAccounts"
                        :value="item.id"
                        :key="item.id">{{ item.name }}</option>
              </select>
              <template v-if="$v.form.withdrawalBankAccountId.$error">
                <div class="form-input-hint">Campo obrigatório</div>
              </template>
            </div>
            <div class="column col-12 form-group">
              <label class="form-label">Observações do fechamento</label>
              <textarea class="form-input" v-model="form.notes" rows="2"></textarea>
            </div>
          </div>
        </div>
      </template>
    </template>

    <template slot="footer">
      <template v-if="closed">
        <button class="btn btn-primary btn-icon btn-icon-left mr-1"
                :class="{loading: printing}" :disabled="printing" @click="printReport">
          <fa-icon :icon="['fal', 'print']"/>Imprimir fechamento
        </button>
        <button class="btn" @click="close()">Sair</button>
      </template>
      <template v-else>
        <button class="btn btn-primary btn-icon btn-icon-left mr-1"
                v-if="blind && step === 1" @click="reveal">
          <fa-icon :icon="['fal', 'calculator']"/>Conferir
        </button>
        <button class="btn btn-error btn-icon btn-icon-left mr-1"
                v-else
                :class="{loading: saving}" :disabled="saving" @click="closeSession">
          <fa-icon :icon="['fal', 'lock']"/>Fechar caixa
        </button>
        <button class="btn" :disabled="saving" @click="close()">Sair</button>
      </template>
    </template>
  </dx-modal>
</template>

<script>
import { round } from 'lodash';
import { required, minValue, maxValue } from 'vuelidate/lib/validators';

export default {
  props: {
    show: {
      type: Boolean,
      default: false,
    },
    session: {
      type: Object,
      required: true,
    },
    blind: {
      type: Boolean,
      default: true,
    },
    tolerance: {
      type: Number,
      default: 0,
    },
  },
  data() {
    return {
      saving: false,
      printing: false,
      step: this.blind ? 1 : 2,
      closed: null,
      bankAccounts: [],
      counted: {},
      form: {
        countedCash: 0,
        keptAsFund: 0,
        withdrawalBankAccountId: '',
        notes: '',
      },
    };
  },
  computed: {
    expectedCash() {
      return this.session.totals.cashBalance;
    },
    netReceipts() {
      const { totals } = this.session;
      return typeof totals.netReceipts === 'number' ? totals.netReceipts : totals.receipts;
    },
    cashDifference() {
      return round(this.form.countedCash - this.expectedCash, 2);
    },
    countState() {
      const informed = this.blind ? this.step === 2 : this.$v.form.countedCash.$dirty;
      if (!informed) return null;
      const diff = Math.abs(this.cashDifference);
      if (diff === 0) return 'ok';
      if (diff <= (Number(this.tolerance) || 0)) return 'warn';
      return 'error';
    },
    countStateClass() {
      return {
        done: this.countState === 'ok',
        warn: this.countState === 'warn',
        error: this.countState === 'error',
      };
    },
    otherMethods() {
      return (this.session.totals.byMethod || []).filter(({ method }) => method.type !== 'money');
    },
    withdrawalValue() {
      return round(Math.max(this.form.countedCash - this.form.keptAsFund, 0), 2);
    },
  },
  validations() {
    return {
      form: {
        countedCash: { required, minValue: minValue(0) },
        keptAsFund: { required, minValue: minValue(0), maxValue: maxValue(this.form.countedCash) },
        withdrawalBankAccountId: this.withdrawalValue > 0 ? { required } : {},
      },
    };
  },
  watch: {
    'form.countedCash': function onCountedCash(value, previous) {
      if (this.blind) return;
      if (this.form.keptAsFund === previous || this.form.keptAsFund > value) {
        this.form.keptAsFund = value;
      }
    },
  },
  mounted() {
    this.loadBankAccounts();
    this.otherMethods.forEach((line) => {
      this.$set(this.counted, line.method.id, line.value);
    });
  },
  methods: {
    recount() {
      this.step = 1;
      this.$v.form.countedCash.$reset();
    },
    loadBankAccounts() {
      const params = { active: true, limit: 0 };
      this.$http.get('/bank-accounts', { params })
        .then(({ data }) => {
          this.bankAccounts = data.items.filter(({ id }) => id !== this.session.bankAccount.id);
        })
        .catch(() => {});
    },
    reveal() {
      this.$v.form.countedCash.$touch();
      if (this.$v.form.countedCash.$error) return;
      this.form.keptAsFund = this.form.countedCash;
      this.step = 2;
    },
    syncWithdrawal() {
      if (this.form.keptAsFund > this.form.countedCash) {
        this.form.keptAsFund = this.form.countedCash;
      }
    },
    differenceClass(value) {
      if (round(value, 2) === 0) return 'text-success';
      return 'text-error';
    },
    printReport() {
      this.printing = true;
      this.$file.print(`/cash-register/sessions/${this.closed.id}/report`)
        .catch(() => {})
        .finally(() => {
          this.printing = false;
        });
    },
    differences() {
      const lines = [];
      if (this.cashDifference !== 0) {
        lines.push({
          name: 'Dinheiro em caixa',
          expected: this.expectedCash,
          counted: this.form.countedCash,
          value: this.cashDifference,
        });
      }
      this.otherMethods.forEach((line) => {
        const counted = Number(this.counted[line.method.id]) || 0;
        const diff = round(counted - line.value, 2);
        if (diff !== 0) {
          lines.push({
            name: line.method.name,
            expected: line.value,
            counted,
            value: diff,
          });
        }
      });
      return lines;
    },
    closeSession() {
      this.$v.form.$touch();
      if (this.$v.form.$error) return;

      const differences = this.differences();
      if (differences.length === 0) {
        this.submitClose();
        return;
      }

      const { currency } = this.$options.filters;
      const rows = differences.map(({
        name, expected, counted, value,
      }) => (
        '<tr>'
        + `<td>${name}</td>`
        + `<td class="text-right">${currency(expected)}</td>`
        + `<td class="text-right">${currency(counted)}</td>`
        + `<td class="text-right text-bold ${value < 0 ? 'text-error' : 'text-warning'}">`
        + `${value > 0 ? '+ ' : ''}${currency(value)}</td>`
        + '</tr>'
      )).join('');

      this.$dialog.show('Fechar com diferença?', {
        size: 'md',
        html: '<div class="cr-close-confirm">'
          + '<p>Os valores informados não batem com o esperado.</p>'
          + '<table class="table">'
          + '<thead><tr><th>Forma</th><th class="text-right">Esperado</th>'
          + '<th class="text-right">Informado</th><th class="text-right">Diferença</th></tr></thead>'
          + `<tbody>${rows}</tbody>`
          + '</table>'
          + '<p class="cr-close-confirm-note">'
          + 'A diferença fica registrada no fechamento e aparece para a tesouraria.'
          + '</p>'
          + '</div>',
        buttons: [
          { label: 'Revisar', classes: '' },
          {
            label: 'Fechar mesmo assim',
            classes: 'btn-error ml-2',
            click: (close) => {
              close();
              this.submitClose();
            },
          },
        ],
      });
    },
    submitClose() {
      const data = {
        countedCash: this.form.countedCash,
        keptAsFund: this.form.keptAsFund,
        counted: this.otherMethods.map(line => ({
          methodId: line.method.id,
          value: Number(this.counted[line.method.id]) || 0,
        })),
        withdrawal: this.withdrawalValue > 0
          ? { bankAccountId: this.form.withdrawalBankAccountId, value: this.withdrawalValue }
          : null,
        notes: this.form.notes || null,
      };

      this.saving = true;
      this.$http.post('/cash-register/session/close', data)
        .then(({ data: session }) => {
          this.closed = session;
          this.$toast.success('Caixa fechado.');
        })
        .catch((e) => {
          this.$toast.error(e);
        })
        .finally(() => {
          this.saving = false;
        });
    },
    close() {
      this.$emit('close', !!this.closed);
    },
  },
};
</script>

<style lang="scss">
@import "./src/assets/scss/_variables.scss";
.cr-close-confirm {
  p {
    margin-bottom: $layout-spacing-sm;
  }
  .table {
    background-color: $gray-color-ultra-light;
    border: $border-color solid $border-width;
    border-radius: $border-radius;
    margin-bottom: $layout-spacing;
    th, td {
      font-size: $font-size-sm;
      padding-left: $layout-spacing;
      padding-right: $layout-spacing;
    }
    thead th {
      border-bottom: $border-color solid $border-width;
      color: $gray-color-dark;
    }
    tbody tr:last-child td {
      border-bottom: 0;
    }
  }
  .cr-close-confirm-note {
    color: $gray-color-dark;
    font-size: $font-size-sm;
    margin: 0;
  }
}
#modal-cash-register-close {
  .cr-count {
    align-items: center;
    display: flex;
    gap: $layout-spacing-lg;
    padding: $layout-spacing-lg;
    .cr-count-text {
      flex: 1 1 auto;
      .cr-box-title {
        margin-bottom: .2rem;
      }
    }
    .cr-count-hint {
      color: $gray-color-dark;
      font-size: $font-size-sm;
      margin: 0;
    }
    .cr-count-field {
      flex: 0 0 15rem;
      margin-bottom: 0;
      .input-group-addon {
        color: $gray-color-dark;
      }
    }
    &.done {
      .cr-count-hint {
        color: darken($success-color, 15%);
      }
    }
    &.warn {
      background-color: #FFF8E6;
      border-color: #F2C94C;
      .cr-count-hint {
        color: $dark-color;
      }
      .cr-count-hint svg {
        color: darken($warning-color, 15%);
      }
    }
    &.error {
      background-color: $red-color-100;
      border-color: $red-color-500;
      .cr-count-hint {
        color: $dark-color;
      }
      .cr-count-hint svg {
        color: $error-color;
      }
      .cr-count-field .form-input,
      .cr-count-field .input-group-addon {
        border-color: $red-color-500;
      }
    }
    @media screen and (max-width: $size-sm) {
      flex-direction: column;
      align-items: stretch;
      .cr-count-field {
        flex: 1 1 auto;
      }
    }
  }
}
</style>
